/**
 * newDealAutofill.ts — slice 2 of the New Deal sheet (spec §3.1 as corrected
 * by ADOPTION.md §18; RULED 2026-10-06): the pure rules behind what the sheet
 * fills in once an EXISTING account is picked. Every input is what ONE read
 * of GET /v1/sales/accounts/{id} returns (openDeals, contacts, closedDeals);
 * nothing here fetches, and nothing here invents a field.
 *
 *   - OWNER: the owner of the account's open deal — the most recent one when
 *     there are several — and only when that deal is linked to an employee.
 *     There is NO "account owner": accounts carry no owner field, so the
 *     spec's "✦ Account owner" branch has nothing honest to read and is not
 *     built.
 *   - CONTACT: the only contact at the account, or the single most senior by
 *     title (CEO > C-level > VP > Director). A tie at the top, or no ranked
 *     title among several contacts, fills nothing: "the most senior" must be
 *     one person, or the tag would state something the data does not.
 *   - TITLE CHIPS: three suggestions from the company name, Expansion first
 *     when the account has a WON deal, else New business first.
 */
import type { SalesContact, SalesDeal } from '../../types'

export const TAG_OWNS_OPEN_DEAL = 'Owns the open deal here'
export const TAG_ONLY_CONTACT = 'Only contact at account'
export const TAG_MOST_SENIOR = 'Most senior contact'

/** Seniority from a free-text title: 4 CEO, 3 other C-level, 2 VP,
 *  1 Director, 0 unranked. Word-bounded and case-insensitive, so "Vice
 *  President" and "VP" agree and "Directory Manager" is not a director. */
export function seniorityRank(title: string | undefined | null): number {
  const t = (title ?? '').toLowerCase()
  if (/\bceo\b|\bchief executive\b/.test(t)) return 4
  if (/\bc[a-z]{1,2}o\b|\bchief\b/.test(t)) return 3
  if (/\bvp\b|\bvice[- ]president\b/.test(t)) return 2
  if (/\bdirector\b/.test(t)) return 1
  return 0
}

export interface ContactPick { contactId: string; tag: string }

export function pickContact(contacts: readonly SalesContact[]): ContactPick | null {
  if (contacts.length === 1) return { contactId: contacts[0].id, tag: TAG_ONLY_CONTACT }
  if (contacts.length === 0) return null
  const ranked = contacts.map(c => ({ c, r: seniorityRank(c.title) }))
  const top = Math.max(...ranked.map(x => x.r))
  if (top === 0) return null
  const atTop = ranked.filter(x => x.r === top)
  if (atTop.length !== 1) return null
  return { contactId: atTop[0].c.id, tag: TAG_MOST_SENIOR }
}

/** The open deal the duplicate card names: the most recently CREATED, and how
 *  many others are open. A deal with no creation stamp sorts last; the
 *  server's own order breaks ties. */
export function mostRecentOpen(openDeals: readonly SalesDeal[]): { deal: SalesDeal; more: number } | null {
  if (openDeals.length === 0) return null
  const sorted = openDeals.map((d, i) => ({ d, i })).sort((a, b) => {
    const ta = a.d.createdAt ? Date.parse(a.d.createdAt) : -Infinity
    const tb = b.d.createdAt ? Date.parse(b.d.createdAt) : -Infinity
    return tb !== ta ? tb - ta : a.i - b.i
  })
  return { deal: sorted[0].d, more: openDeals.length - 1 }
}

export interface OwnerPick { employeeId: string; tag: string }

export function pickOwner(openDeals: readonly SalesDeal[]): OwnerPick | null {
  const recent = mostRecentOpen(openDeals)
  const id = recent?.deal.assignedEmployeeId
  return id ? { employeeId: id, tag: TAG_OWNS_OPEN_DEAL } : null
}

export const CHIP_SUFFIXES = ['Expansion', 'New business', 'Renewal'] as const

export function titleChips(company: string, hasWonDeal: boolean): string[] {
  const name = company.trim()
  if (name === '') return []
  const order = hasWonDeal ? ['Expansion', 'New business', 'Renewal'] : ['New business', 'Expansion', 'Renewal']
  return order.map(s => `${name} – ${s}`)
}

export function hasWonDeal(closedDeals: readonly SalesDeal[]): boolean {
  return closedDeals.some(d => d.stage === 'Closed Won')
}

/**
 * The autofill decision for ONE field, given what the field holds now:
 *   - a field the user EDITED is never touched;
 *   - an EMPTY field takes the pick;
 *   - a field still carrying an autofill TAG (it came from a previous
 *     account, untouched since) takes the new pick, or clears when the new
 *     account offers none;
 *   - anything else (a restored draft value, say) is left alone.
 * Returns the next {value, tag}, or null for "leave it".
 */
export function autofillField(
  current: { value: string; tag: string | null; edited: boolean },
  pick: { value: string; tag: string } | null,
): { value: string; tag: string | null } | null {
  if (current.edited) return null
  if (current.value === '' || current.tag !== null) {
    if (pick) return { value: pick.value, tag: pick.tag }
    return current.tag !== null ? { value: '', tag: null } : null
  }
  return null
}
